import React, { useEffect, useRef, useState } from 'react'
import xss from 'xss'
import {
  Button,
  Input,
  Notification,
  NotificationType,
  Content,
  Textarea
} from '@ampcore/adel-react'
import {
  isValidEmail,
  formatPhoneNumber,
  isValidPhoneNumber
} from '../../../common/utils'
import { formSelectedData, CONSTs } from '../views/utils'
import { FaceProfileData } from '@ampcore/client-side-services'

export type IRAFieldName = 'fullname' | 'emailaddress' | 'phone' | 'inquiry'

export type IRAFormData = {
  fullname: string | null
  emailaddress: string | null
  inquiry: string | null
  phone: string | null
  resultDownload: string | null
}

export type IRAErrorFields = {
  fullname: boolean
  emailaddress: boolean
  inquiry: boolean
  phone: boolean
  emailWrongFormat: boolean
}

export type IRAFormLabels = {
  fullname: string
  emailaddress: string
  phone: string
  inquiry: string
  buttonlabel: string
  buttonLoadingLabel: string
}

export type IRAFormHeading = {
  title?: string
  beforename?: string
}

export type IRAFormEmailInfo = {
  emailApi: string
  subject: string
  toEmailAddress: string
}

export type IRAFormErrorMessages = {
  errorFullname: string
  errorEmailaddress: string
  errorEmailaddressWrongFormat: string
  errorPhone?: string
}

export type IRAServiceMessages = {
  serviceSuccess: string
  serviceSuccessThankyou: string
  serviceFailed: string
}

export type IRAFormPleaseNote = {
  message: string
}

export type IRAFormTemplate = {
  advisorselector: string
  advisorevent: string
  resultsselector: string
  resultsevent: string
  modalselector: string
}

export type IRAShowServerResponse = (
  props: unknown,
  message?: string
) => boolean

export type ContactFormIRAContent = {
  formLabels: IRAFormLabels
  formHeading?: IRAFormHeading
  formEmailInfo: IRAFormEmailInfo
  formErrorMessages: IRAFormErrorMessages
  serviceMessages?: Partial<IRAServiceMessages>
  formPleaseNote?: IRAFormPleaseNote
  formTemplate: IRAFormTemplate
}

export type IRACallback = (
  items: unknown,
  emailInfo: IRAFormEmailInfo,
  showServerResponse: IRAShowServerResponse
) => unknown

export type ContactFormIRAProps = {
  callback?: IRACallback
  name?: string
  content: ContactFormIRAContent
  testId?: string
}

const ContactFormIRA = (props: ContactFormIRAProps) => {
  const { callback, name, content, testId } = props

  const [formData, setFormData] = useState<IRAFormData>({
    fullname: null,
    emailaddress: null,
    inquiry: null,
    phone: null,
    resultDownload: null
  })

  const [errorFields, setErrorFields] = useState<IRAErrorFields>({
    fullname: false,
    emailaddress: false,
    inquiry: false,
    phone: false,
    emailWrongFormat: false
  })

  const [loading, setLoading] = useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [showServiceFailedMessage, setShowServiceFailedMessage] =
    useState(false)
  const [advisorName, setAdvisorName] = useState('')
  const [advisorEmail, setAdvisorEmail] = useState('')

  const fullnameRef = useRef<HTMLInputElement | null>(null)
  const emailaddressRef = useRef<HTMLInputElement | null>(null)
  const phoneRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    const { advisorevent, resultsselector, resultsevent } = content.formTemplate

    const handleAdvisorEvent = (event: Event) => {
      const profile = (event as CustomEvent<FaceProfileData>).detail
      if (profile) {
        setAdvisorName(profile.name || '')
        setAdvisorEmail(profile.email || '')
      }
    }

    window.addEventListener(advisorevent, handleAdvisorEvent)

    const resultsEl = document.querySelector(resultsselector)
    const handleResultsEvent = (event: Event) => {
      const e = event as CustomEvent<{ html: Promise<string> }>
      e.detail?.html
        ?.then(pdfHtml => {
          setFormData(prev => ({ ...prev, resultDownload: pdfHtml }))
        })
        .catch(console.error)
    }
    if (resultsEl) {
      resultsEl.addEventListener(resultsevent, handleResultsEvent)
    }

    return () => {
      window.removeEventListener(advisorevent, handleAdvisorEvent)
      if (resultsEl)
        resultsEl.removeEventListener(resultsevent, handleResultsEvent)
    }
  }, [content.formTemplate])

  const handleHide = () => {
    const modal = document.querySelector(content.formTemplate.modalselector)
    if (modal) {
      (modal as HTMLElement).style.display = 'none'
    }
    // Restore background scroll on Cancel.
    const html = document.querySelector('html')
    if (html) {
      html.style.overflow = 'auto'
    }
    setShowSuccessMessage(false)
  }

  const showServerResponse: IRAShowServerResponse = (props, message) => {
    if (props && message) {
      if (message === CONSTs.SUCCESS) {
        setLoading(false)
        setShowSuccessMessage(true)
        setShowServiceFailedMessage(false)
        const modal = document.querySelector(content.formTemplate.modalselector)
        if (modal) {
          modal.classList.remove('Modal--fullScreen')
          ;(modal as HTMLElement).style.margin = '0'
          modal
            .querySelector('.Modal-window')
            ?.classList.add('u-posAbsoluteCenter')
          modal.querySelector('.close-modal')?.classList.remove('u-hidden')
        }
      } else {
        setLoading(false)
        setShowSuccessMessage(false)
        setShowServiceFailedMessage(true)
      }
    }
    return true
  }

  const getFormStatus = (): boolean => {
    for (const key in formData) {
      const typedKey = key as keyof IRAFormData
      if (typedKey === 'inquiry' || typedKey === 'phone') continue
      if (formData[typedKey] === null) return false
    }
    return true
  }

  const validateField = (fieldName: IRAFieldName, value: string) => {
    const fieldErrors: IRAErrorFields = { ...errorFields }
    const updatedFormData: IRAFormData = { ...formData }
    const isNotEmpty = value.trim().length > 0

    switch (fieldName) {
    case 'fullname': {
      fieldErrors.fullname = !isNotEmpty
      updatedFormData.fullname = isNotEmpty ? value : null
      break
    }
    case 'emailaddress': {
      const isEmailValid = isValidEmail(value)
      fieldErrors.emailaddress = !isEmailValid
      fieldErrors.emailWrongFormat = !isEmailValid && isNotEmpty
      updatedFormData.emailaddress = isEmailValid ? value : null
      break
    }
    case 'phone': {
      const isValidPhone = isValidPhoneNumber(value, true)
      fieldErrors.phone = !isValidPhone
      if (isValidPhone && isNotEmpty) {
        const formatted = formatPhoneNumber(value)
        if (phoneRef.current) {
          phoneRef.current.value = formatted
        }
        updatedFormData.phone = formatted
      } else {
        updatedFormData.phone = isNotEmpty ? null : updatedFormData.phone
      }
      break
    }
    case 'inquiry': {
      updatedFormData.inquiry = value || null
      break
    }
    }

    if (fieldName && value !== undefined) {
      setFormData(updatedFormData)
      setErrorFields(fieldErrors)
    }
  }

  const handleFieldChange = (event: React.SyntheticEvent<HTMLElement>) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | null
    if (!target || !target.name) return
    validateField(target.name as IRAFieldName, target.value || '')
  }

  const submitData = async (event: { preventDefault: () => void }) => {
    event.preventDefault()

    const canFormBeSubmitted = getFormStatus()

    if (canFormBeSubmitted) {
      setLoading(true)
      const items = formSelectedData(formData, content.formTemplate)
      const updatedEmailInfo: IRAFormEmailInfo = {
        ...content.formEmailInfo,
        toEmailAddress: advisorEmail || content.formEmailInfo.toEmailAddress
      }
      if (callback && typeof callback === 'function' && items) {
        return callback(items, updatedEmailInfo, showServerResponse)
      }
      return true
    }

    const formFullname = fullnameRef.current?.value || ''
    const formEmailAddress = emailaddressRef.current?.value || ''
    const formPhoneNumber = phoneRef.current?.value || ''
    const fieldValues: IRAFormData = { ...formData }
    const fieldErrors: IRAErrorFields = { ...errorFields }

    if (formFullname.length < 1) {
      fieldValues.fullname = null
      fieldErrors.fullname = true
    } else {
      fieldValues.fullname = formData.fullname
      fieldErrors.fullname = false
    }

    if (formEmailAddress) {
      const cleanedText = formEmailAddress.toLowerCase()
      fieldValues.emailaddress = isValidEmail(cleanedText)
        ? formEmailAddress
        : null
      fieldErrors.emailaddress = !fieldValues.emailaddress
    } else {
      fieldErrors.emailaddress = true
    }

    if (formPhoneNumber) {
      const isValidPhone = isValidPhoneNumber(formPhoneNumber, true)
      fieldValues.phone = isValidPhone ? formPhoneNumber : null
      fieldErrors.phone = !isValidPhone
    }

    setFormData(fieldValues)
    setErrorFields(fieldErrors)
    return false
  }

  const setEmailErrorMessage = (
    wrongEmail: boolean,
    message: IRAFormErrorMessages
  ): string => {
    return wrongEmail
      ? message.errorEmailaddressWrongFormat
      : message.errorEmailaddress
  }

  const successChildren = (
    <>
      <h3 className="u-paddingBottom16">
        {content?.serviceMessages?.serviceSuccessThankyou}
      </h3>
      <p
        className="u-paddingTop16 u-paddingBottom16"
        dangerouslySetInnerHTML={{
          __html: xss(content?.serviceMessages?.serviceSuccess ?? '')
        }}
      />
    </>
  )

  return (
    <div className="u-marginTop8">
      {showSuccessMessage && content.serviceMessages?.serviceSuccess && (
        <div className="u-sizeConstrained" role="alert">
          <Notification
            type={NotificationType.CONFIRM}
            dismissable={false}
            className="IRAFormNotification u-sizeConstrained"
            children={successChildren}
          />
        </div>
      )}
      {!showSuccessMessage && (
        <div className="u-sizeConstrained">
          <form
            name={name}
            className="contact-ira u-marginBottom24"
            onBlur={handleFieldChange}
            onKeyUp={handleFieldChange}
          >
            {content.formHeading?.beforename && advisorName && (
              <h5 className="u-marginBottom4">
                {content.formHeading.beforename + ' ' + advisorName}
              </h5>
            )}
            {content.formHeading?.title && (
              <h4 className="u-marginBottom8">{content.formHeading.title}</h4>
            )}
            <Input
              name="fullname"
              id="fullname"
              label={content.formLabels.fullname}
              reference={fullnameRef}
              error={errorFields.fullname}
              message={content.formErrorMessages.errorFullname}
              testId={testId ? `${testId}-fullname` : undefined}
            />
            <Input
              name="phone"
              id="phone"
              label={content.formLabels.phone}
              reference={phoneRef}
              error={errorFields.phone}
              message={content.formErrorMessages.errorPhone}
              testId={testId ? `${testId}-phone` : undefined}
            />
            <Input
              name="emailaddress"
              id="email"
              label={content.formLabels.emailaddress}
              reference={emailaddressRef}
              error={errorFields.emailaddress}
              message={setEmailErrorMessage(
                errorFields.emailWrongFormat,
                content.formErrorMessages
              )}
              testId={testId ? `${testId}-email` : undefined}
            />
            <Textarea
              name="inquiry"
              id="inquiry"
              label={content.formLabels.inquiry}
              defaultValue={content.formLabels.inquiry}
              row={3}
              testId={testId ? `${testId}-inquiry` : undefined}
            />
            {showServiceFailedMessage &&
              content.serviceMessages?.serviceFailed && (
              <Notification
                type={NotificationType.ALERT}
                message={content.serviceMessages.serviceFailed}
                dismissable={false}
                className="u-marginBottom24"
              />
            )}
            <div className="ButtonRow">
              <Button
                onClick={submitData}
                isLoading={loading}
                data-analytics={'Button'}
                data-tracking={'Button'}
                children={
                  loading
                    ? content.formLabels.buttonLoadingLabel
                    : content.formLabels.buttonlabel
                }
              />
              <button
                className="Button Button--secondary Button--secondary--action-blue"
                type="button"
                onClick={handleHide}
              >
                Cancel
              </button>
            </div>
          </form>
          {content.formPleaseNote?.message && (
            <Content className={'u-marginBottom24'}>
              <div
                className="Content"
                dangerouslySetInnerHTML={{
                  __html: xss(content.formPleaseNote.message)
                }}
              />
            </Content>
          )}
        </div>
      )}
    </div>
  )
}

export default ContactFormIRA
